<template>
  <div class="relative" ref="containerRef">
    <!-- 触发按钮 -->
    <button
      @click="showPanel = !showPanel"
      class="inline-flex items-center h-10 px-3 w-full text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 whitespace-nowrap overflow-hidden"
    >
      <CalendarIcon class="h-4 w-4 mr-2 text-gray-400" />
      <span v-if="displayText">{{ displayText }}</span>
      <span v-else class="text-gray-400">选择日期范围</span>
      <XMarkIcon v-if="modelValue.start" class="h-4 w-4 ml-2 text-gray-400 hover:text-gray-600" @click.stop="clearSelection" />
    </button>

    <!-- 弹出面板 -->
    <div v-if="showPanel" class="absolute top-full right-0 mt-1 z-50 bg-white rounded-lg border border-gray-200 shadow-xl flex" style="min-width: 600px;">
      <!-- 左侧快捷选项 -->
      <div class="w-32 border-r border-gray-100 py-2 max-h-80 overflow-y-auto">
        <button
          v-for="shortcut in shortcuts"
          :key="shortcut.label"
          @click="applyShortcut(shortcut)"
          class="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 hover:text-blue-600 transition-colors"
          :class="activeShortcut === shortcut.label ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-600'"
        >
          {{ shortcut.label }}
        </button>
      </div>

      <!-- 右侧双月日历 -->
      <div class="flex p-3 gap-4">
        <div v-for="offset in [0, 1]" :key="offset" class="w-56">
          <!-- 月份头部 -->
          <div class="flex items-center justify-between mb-2">
            <button v-if="offset === 0" @click="prevMonth" class="p-1 hover:bg-gray-100 rounded">
              <ChevronLeftIcon class="h-4 w-4 text-gray-500" />
            </button>
            <span v-else class="w-6"></span>
            <span class="text-sm font-medium text-gray-700">{{ getMonthTitle(offset) }}</span>
            <button v-if="offset === 1" @click="nextMonth" class="p-1 hover:bg-gray-100 rounded">
              <ChevronRightIcon class="h-4 w-4 text-gray-500" />
            </button>
            <span v-else class="w-6"></span>
          </div>
          <!-- 星期头 -->
          <div class="grid grid-cols-7 mb-1">
            <span v-for="d in weekDays" :key="d" class="text-center text-xs text-gray-400 py-1">{{ d }}</span>
          </div>
          <!-- 日期格子 -->
          <div class="grid grid-cols-7">
            <button
              v-for="day in getMonthDays(offset)"
              :key="day.key"
              @click="day.date && selectDate(day.date)"
              @mouseenter="day.date && onHover(day.date)"
              class="h-7 w-7 mx-auto text-xs rounded-full flex items-center justify-center transition-colors"
              :class="getDayClass(day)"
              :disabled="!day.date"
            >
              {{ day.label }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import dayjs from 'dayjs'
import { CalendarIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  modelValue: { type: Object, default: () => ({ start: '', end: '' }) }
})
const emit = defineEmits(['update:modelValue'])

const showPanel = ref(false)
const containerRef = ref(null)
const baseMonth = ref(dayjs().startOf('month'))
const hoverDate = ref(null)
const tempStart = ref(null)
const activeShortcut = ref('')
const weekDays = ['日', '一', '二', '三', '四', '五', '六']

const shortcuts = [
  { label: '今日', fn: () => [dayjs(), dayjs()] },
  { label: '昨日', fn: () => [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')] },
  { label: '最近3天', fn: () => [dayjs().subtract(2, 'day'), dayjs()] },
  { label: '最近7天', fn: () => [dayjs().subtract(6, 'day'), dayjs()] },
  { label: '最近14天', fn: () => [dayjs().subtract(13, 'day'), dayjs()] },
  { label: '最近30天', fn: () => [dayjs().subtract(29, 'day'), dayjs()] },
  { label: '最近60天', fn: () => [dayjs().subtract(59, 'day'), dayjs()] },
  { label: '最近90天', fn: () => [dayjs().subtract(89, 'day'), dayjs()] },
  { label: '本月', fn: () => [dayjs().startOf('month'), dayjs().endOf('month')] },
  { label: '上月', fn: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { label: '最近半年', fn: () => [dayjs().subtract(6, 'month'), dayjs()] },
  { label: '今年', fn: () => [dayjs().startOf('year'), dayjs()] },
]

const displayText = computed(() => {
  if (!props.modelValue.start) return ''
  return `${props.modelValue.start} ~ ${props.modelValue.end}`
})

const getMonthTitle = (offset) => {
  const m = baseMonth.value.add(offset, 'month')
  return `${m.year()}年${m.month() + 1}月`
}

const prevMonth = () => { baseMonth.value = baseMonth.value.subtract(1, 'month') }
const nextMonth = () => { baseMonth.value = baseMonth.value.add(1, 'month') }

const getMonthDays = (offset) => {
  const month = baseMonth.value.add(offset, 'month')
  const startOfMonth = month.startOf('month')
  const daysInMonth = month.daysInMonth()
  const startDay = startOfMonth.day()
  const days = []
  for (let i = 0; i < startDay; i++) days.push({ key: `e${offset}-${i}`, label: '', date: null })
  for (let i = 1; i <= daysInMonth; i++) {
    const date = month.date(i)
    days.push({ key: `d${offset}-${i}`, label: i, date })
  }
  return days
}

const selectDate = (date) => {
  if (!tempStart.value) {
    tempStart.value = date
  } else {
    let start = tempStart.value
    let end = date
    if (end.isBefore(start)) { [start, end] = [end, start] }
    emit('update:modelValue', { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') })
    tempStart.value = null
    hoverDate.value = null
    activeShortcut.value = ''
    showPanel.value = false
  }
}

const onHover = (date) => { if (tempStart.value) hoverDate.value = date }

const getDayClass = (day) => {
  if (!day.date) return 'cursor-default'
  const d = day.date
  const start = tempStart.value || (props.modelValue.start ? dayjs(props.modelValue.start) : null)
  const end = hoverDate.value || (props.modelValue.end ? dayjs(props.modelValue.end) : null)
  if (start && end) {
    const s = start.isBefore(end) ? start : end
    const e = start.isBefore(end) ? end : start
    if (d.isSame(s, 'day') || d.isSame(e, 'day')) return 'bg-blue-500 text-white cursor-pointer'
    if (d.isAfter(s, 'day') && d.isBefore(e, 'day')) return 'bg-blue-100 text-blue-700 cursor-pointer'
  } else if (start && d.isSame(start, 'day')) {
    return 'bg-blue-500 text-white cursor-pointer'
  }
  return 'hover:bg-gray-100 cursor-pointer text-gray-700'
}

const applyShortcut = (shortcut) => {
  const [start, end] = shortcut.fn()
  activeShortcut.value = shortcut.label
  emit('update:modelValue', { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') })
  tempStart.value = null
  showPanel.value = false
}

const clearSelection = () => {
  emit('update:modelValue', { start: '', end: '' })
  activeShortcut.value = ''
  tempStart.value = null
}

const handleClickOutside = (e) => {
  if (containerRef.value && !containerRef.value.contains(e.target)) showPanel.value = false
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside))
</script>
